import type { RockPaperScissorsGesture } from "@topzle/game-rock-paper-scissors";

export interface RockPaperScissorsRecognition {
  gesture: RockPaperScissorsGesture | null;
  confidence: number;
  landmarks: Array<{ x: number; y: number }>;
}

interface WorkerSuccess {
  id: number;
  ok: true;
  categoryName: string | null;
  confidence: number;
  landmarks: Array<{ x: number; y: number }>;
}

interface WorkerFailure {
  id: number;
  ok: false;
  error: string;
}

type WorkerResponse = WorkerSuccess | WorkerFailure;

interface PendingRequest {
  resolve: (result: RockPaperScissorsRecognition) => void;
  reject: (error: Error) => void;
}

let recognizerWorker: Worker | null = null;
let nextRequestId = 1;
const pendingRequests = new Map<number, PendingRequest>();
let recognitionBackend: "worker" | "main" | null = null;
let mainThreadRecognizerPromise: Promise<
  import("@mediapipe/tasks-vision").GestureRecognizer
> | null = null;

export function shouldUseMainThreadMediaPipe(
  userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent,
  platform = typeof navigator === "undefined" ? "" : navigator.platform,
  touchPoints = typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints,
): boolean {
  return /iPad|iPhone|iPod/i.test(userAgent) || (platform === "MacIntel" && touchPoints > 1);
}

export function mediaPipeGestureToRps(
  categoryName: string | null,
): RockPaperScissorsGesture | null {
  if (categoryName === "Closed_Fist") return "rock";
  if (categoryName === "Open_Palm") return "paper";
  if (categoryName === "Victory") return "scissors";
  return null;
}

function rejectPending(message: string) {
  for (const request of pendingRequests.values()) request.reject(new Error(message));
  pendingRequests.clear();
}

function getWorker(): Worker {
  if (typeof Worker === "undefined")
    throw new Error("Live hand recognition requires a modern browser");
  if (recognizerWorker) return recognizerWorker;
  const worker = new Worker(new URL("./rpsGestureRecognizer.worker.ts", import.meta.url), {
    type: "module",
    name: "topzle-rps-mediapipe",
  });
  worker.addEventListener("message", (event: MessageEvent<WorkerResponse>) => {
    const response = event.data;
    const pending = pendingRequests.get(response.id);
    if (!pending) return;
    pendingRequests.delete(response.id);
    if (!response.ok) {
      pending.reject(new Error(response.error));
      return;
    }
    pending.resolve({
      gesture: mediaPipeGestureToRps(response.categoryName),
      confidence: response.confidence,
      landmarks: response.landmarks,
    });
  });
  worker.addEventListener("error", (event) => {
    rejectPending(event.message || "The MediaPipe hand recognizer stopped unexpectedly");
    worker.terminate();
    if (recognizerWorker === worker) recognizerWorker = null;
  });
  recognizerWorker = worker;
  return worker;
}

function getMainThreadRecognizer() {
  if (mainThreadRecognizerPromise) return mainThreadRecognizerPromise;
  mainThreadRecognizerPromise = import("@mediapipe/tasks-vision")
    .then(async ({ FilesetResolver, GestureRecognizer }) => {
      const fileset = await FilesetResolver.forVisionTasks(
        `${window.location.origin}/models/rock-paper-scissors/wasm`,
      );
      return GestureRecognizer.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: `${window.location.origin}/models/rock-paper-scissors/gesture_recognizer.task`,
        },
        runningMode: "IMAGE",
        numHands: 1,
        minHandDetectionConfidence: 0.45,
        minHandPresenceConfidence: 0.45,
        minTrackingConfidence: 0.45,
        cannedGesturesClassifierOptions: {
          maxResults: 1,
          scoreThreshold: 0.42,
          categoryAllowlist: ["Closed_Fist", "Open_Palm", "Victory"],
        },
      });
    })
    .catch((error) => {
      mainThreadRecognizerPromise = null;
      throw error;
    });
  return mainThreadRecognizerPromise;
}

async function recognizeOnMainThread(
  image: ImageBitmap | ImageData,
): Promise<RockPaperScissorsRecognition> {
  const recognizer = await getMainThreadRecognizer();
  const result = recognizer.recognize(image);
  const category = result.gestures[0]?.[0];
  return {
    gesture: mediaPipeGestureToRps(category?.categoryName ?? null),
    confidence: category?.score ?? 0,
    landmarks: (result.landmarks[0] ?? []).map(({ x, y }) => ({ x, y })),
  };
}

function requestRecognition(
  type: "warm" | "recognize",
  image?: ImageBitmap | ImageData,
): Promise<RockPaperScissorsRecognition> {
  const worker = getWorker();
  const id = nextRequestId++;
  return new Promise((resolve, reject) => {
    pendingRequests.set(id, { resolve, reject });
    if (typeof ImageBitmap !== "undefined" && image instanceof ImageBitmap)
      worker.postMessage({ id, type, image }, [image]);
    else if (image) worker.postMessage({ id, type, image });
    else worker.postMessage({ id, type });
  });
}

export async function warmRockPaperScissorsRecognizer(): Promise<void> {
  if (shouldUseMainThreadMediaPipe()) {
    await getMainThreadRecognizer();
    recognitionBackend = "main";
    return;
  }
  try {
    await requestRecognition("warm");
    recognitionBackend = "worker";
  } catch (error) {
    console.warn("RPS MediaPipe worker unavailable; using the compatibility path", error);
    await getMainThreadRecognizer();
    recognitionBackend = "main";
  }
}

export async function recognizeRockPaperScissorsFrame(
  image: ImageBitmap | ImageData,
): Promise<RockPaperScissorsRecognition> {
  if (recognitionBackend === "main" || shouldUseMainThreadMediaPipe())
    return recognizeOnMainThread(image);
  try {
    return await requestRecognition("recognize", image);
  } catch (error) {
    recognitionBackend = "main";
    if (typeof ImageBitmap !== "undefined" && image instanceof ImageBitmap) throw error;
    return recognizeOnMainThread(image);
  }
}
